import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useParams, useRouteMatch } from 'react-router-dom'
import { useDeviceType } from '@calendly/ui/hooks/use-device-type'
import styled from 'astroturf/react'
import { OnetrustSettingsButton } from '@calendly/onetrust/redux'
import { parseQuery } from '@calendly/http-tools'
import { useFlag } from '@calendly/feature-check-react'
import { VisuallyHidden } from '@calendly/ui/components/visually-hidden'
import {
  EventTypeDescription,
  EventTypeLocation,
  Duration,
  Price,
  BackButton,
  MobileHeaderContainer as Container,
  MobileSidebarHeader as Header,
  MobileSidebarDate as Date
} from '@calendly/booking-kit'
import { Loader } from '@calendly/ui/components/loader'

import { formatDate, strftimeToLuxonFormat } from '@calendly/datetime'
import { i18n, useI18n } from 'locales'
import { setEventType, eventTypeSelector } from 'root-api'
import { daySelector, hideEventTypeDetailsSelector } from 'selectors/calendar'
import { currentInviteeSelector } from 'selectors/current_invitee'
import { isPublisherSelector, timeNotationSelector } from 'selectors'
import { nameSelector } from 'selectors/invitee'
import { isEmbeddedSelector } from 'selectors/embed'
import Details from 'components/details'
import FormerDetails from 'components/rescheduling/details'
import { EnterDetailsRescheduleSummary } from 'components/enter_details/reschedule_heading_summary'
import { formatRescheduleEnterDetailsRangeLine } from 'services/spot_time'
import Profile from 'components/profile'
import SidePanel from 'components/layout/side_panel'
import { deselectCalendarDay } from 'actions/calendar_day'
import { TimezonePicker } from 'components/timezone_picker'
import TimezoneSwitch from 'components/select_date_time/timezone_switch'
import { useOrganizationLogoUrl } from 'components/use_organization'
import { useProfile } from 'components/use_profile'
import { eventService } from 'services/analytics/event_service'
import { anonymousIdSelector } from 'selectors/segment'
import { currentUserSelector } from 'selectors/user'
import { usePurchasedPackageData } from 'hooks/usePurchasedPackageData'

import PackageSession from '../details/packages_session'
import { ExpandableEventTypeDescription } from './expandable_event_type_description'

import { buildProfileEventTypesListUrl } from 'utils/booking_profile_back_url'
import { BookingRedesignPhoneBackButton } from 'components/booking_redesign_phone_back_button'
import _ReschedulingDetails from "sidebar-ReschedulingDetails.module.css!=!astroturf/inline-loader?style=1!/home/runner/work/booking/booking/frontend/components/select_date_time/sidebar.js?styleId=ReschedulingDetails";
import _SidebarRescheduleSummaryWrap from "sidebar-SidebarRescheduleSummaryWrap.module.css!=!astroturf/inline-loader?style=1!/home/runner/work/booking/booking/frontend/components/select_date_time/sidebar.js?styleId=SidebarRescheduleSummaryWrap";
import _DetailsContainer from "sidebar-DetailsContainer.module.css!=!astroturf/inline-loader?style=1!/home/runner/work/booking/booking/frontend/components/select_date_time/sidebar.js?styleId=DetailsContainer";
import _PickerContainer from "sidebar-PickerContainer.module.css!=!astroturf/inline-loader?style=1!/home/runner/work/booking/booking/frontend/components/select_date_time/sidebar.js?styleId=PickerContainer";
import _CookieSettingsContainer from "sidebar-CookieSettingsContainer.module.css!=!astroturf/inline-loader?style=1!/home/runner/work/booking/booking/frontend/components/select_date_time/sidebar.js?styleId=CookieSettingsContainer";


const ReschedulingDetails = /*#__PURE__*/styled(FormerDetails, null, {
  displayName: "ReschedulingDetails",
  styles: _ReschedulingDetails
})

const SidebarRescheduleSummaryWrap = /*#__PURE__*/styled('div', null, {
  displayName: "SidebarRescheduleSummaryWrap",
  styles: _SidebarRescheduleSummaryWrap,
  attrs: props => ({
    ...props,
    isPhone: useDeviceType('isPhone'),
    isTablet: useDeviceType('isTablet'),
    isTabletDown: useDeviceType('isTabletDown')
  })
})

const DetailsContainer = /*#__PURE__*/styled('div', null, {
  displayName: "DetailsContainer",
  styles: _DetailsContainer,
  attrs: props => ({
    ...props,
    isPhone: useDeviceType('isPhone'),
    isTablet: useDeviceType('isTablet'),
    isTabletDown: useDeviceType('isTabletDown'),
    isTabletUp: useDeviceType('isTabletUp')
  })
})

const PickerContainer = /*#__PURE__*/styled('div', null, {
  displayName: "PickerContainer",
  styles: _PickerContainer
})

const CookieSettingsContainer = /*#__PURE__*/styled('div', null, {
  displayName: "CookieSettingsContainer",
  styles: _CookieSettingsContainer
})

const MobileHeader = ({ onClickBack }) => {
  const { date } = useSelector((state) => daySelector(state))
  const bookingAppRedesign = useFlag('booking_app_redesign', false)

  return (
    <Container>
      {bookingAppRedesign ? (
        <BookingRedesignPhoneBackButton onClick={onClickBack} />
      ) : (
        <BackButton onClick={onClickBack} />
      )}
      <Header>{formatDate(date, { format: 'EEEE' })}</Header>
      <Date>{formatDate(date, {
        format: strftimeToLuxonFormat(i18n.t('time.formats.date_large')),
        locale: i18n.locale
      })}</Date>
      {!bookingAppRedesign && (
        <PickerContainer>
          <TimezonePicker />
        </PickerContainer>
      )}
      <TimezoneSwitch />
    </Container>
  )
}

const checkIfIframe = () => window.location !== window.parent.location

const RenderProfile = ({ name, avatar, logoUrl }) => {
  const eventType = useSelector(eventTypeSelector)
  const bookingAppRedesign = useFlag('booking_app_redesign', false)
  const { search } = useLocation()
  const { back } = parseQuery(search)
  const { profileSlug } = useParams()
  const { path } = useRouteMatch()

  if (!eventType) return null

  const shouldShowBackButton = parseInt(back, 10) === 1 && eventType.public && path === '/:profileSlug/:eventTypeSlug'
  const showSidebarBackButton = shouldShowBackButton && !bookingAppRedesign
  const nameToShow = eventType.type === 'AdhocEventType' ? eventType.publishers_list : name
  const avatarToShow = eventType.type === 'AdhocEventType' ? eventType.publisher_avatar : avatar

  return (
    <>
      {showSidebarBackButton && (
        <BackButton
          withCompanyLogo={Boolean(logoUrl)}
          as="a"
          href={buildProfileEventTypesListUrl(profileSlug, search)}
        />
      )}
      <Profile
        avatar={avatarToShow}
        name={nameToShow}
        logoUrl={logoUrl}
        eventTypeName={eventType.name}
        sidebar
      />
    </>
  )
}

const RenderOrganization = () => {
  const logoUrl = useOrganizationLogoUrl()

  return (
    <RenderProfile
      logoUrl={logoUrl}
    />
  )
}

const ProfileWrapper = () => {
  const profile = useProfile()

  if (profile === undefined) return <Loader />

  if (profile) {
    return (
      <RenderProfile
        avatar={profile.avatar_url}
        name={profile.name}
        logoUrl={profile.logo_url}
      />
    )
  }

  return <RenderOrganization />
}

const FooterLinks = () => {
  const embedSelector = useSelector(isEmbeddedSelector)
  const isEmbedded = checkIfIframe() || embedSelector

  if (isEmbedded) return <CookieSettingsContainer><OnetrustSettingsButton /></CookieSettingsContainer>

  return (
    <CookieSettingsContainer>
      <OnetrustSettingsButton />
    </CookieSettingsContainer>
  )
}

const Sidebar = ({ showFlatSpotpicker }) => {
  const { i18n: i18nHook } = useI18n()
  const isDaySelected = useSelector((state) => Boolean(daySelector(state)))
  const shouldHideDetails = useSelector(hideEventTypeDetailsSelector)
  const eventType = useSelector(eventTypeSelector)
  const anonymousId = useSelector(anonymousIdSelector)
  const currentUser = useSelector(currentUserSelector)
  const invitee = useSelector(currentInviteeSelector)
  const timeNotation = useSelector(timeNotationSelector)
  const isPublisher = useSelector(isPublisherSelector)
  const inviteeName = useSelector(nameSelector)
  const bookingAppRedesign = useFlag('booking_app_redesign', false)
  const dispatch = useDispatch()
  const isPhone = useDeviceType('isPhone')
  const isTablet = useDeviceType('isTablet')
  const isTabletDown = useDeviceType('isTabletDown')
  const isNarrow = isDaySelected || showFlatSpotpicker
  const durationOptions = eventType.duration_options

  // Fetch purchased package data to display session count
  const { purchasedPackage, currentSession, numSessions } = usePurchasedPackageData(eventType?.uuid)

  const hasMeetingPackage = purchasedPackage || invitee?.event?.package_fulfillment_uuid

  const dateLocale = invitee?.locale || eventType?.locale || i18nHook.locale
  const rescheduleFormerLine = invitee?.event
    ? formatRescheduleEnterDetailsRangeLine(invitee.event, timeNotation, invitee.timezone, dateLocale)
    : ''
  const rescheduleFormerTimeLabel = isPublisher
    ? `${i18nHook.t('booking.templates.former_time')} (${inviteeName})`
    : i18nHook.t('booking.templates.former_time')
  const showRedesignRescheduleSummary =
    bookingAppRedesign && rescheduleFormerLine

  const deselectDay = () => {
    dispatch(deselectCalendarDay())
  }

  const selectDuration = (duration) => {
    dispatch(setEventType({ duration, durationOverride: duration }))
    eventService.dispatch({
      name: 'Modal Interaction',
      version: 1,
      dependencies: { user_uuid: currentUser?.uuid || anonymousId },
      entities: ['user', 'organization'],
      properties: {
        modal_name: 'multiple_durations_options',
        location: 'booking_page',
        action: 'select_duration',
        modal_details: duration,
        host_calendly_user_id: eventType?.owning_user?.id,
        user_id: currentUser?.uuid,
        anonymous_id: anonymousId
      }
    })
  }

  if (isPhone && isDaySelected && !showFlatSpotpicker) return <MobileHeader onClickBack={deselectDay} />
  if (shouldHideDetails) return <VisuallyHidden><h1>{eventType.name}</h1></VisuallyHidden>

  return (
    <SidePanel narrow={isNarrow}>
      <ProfileWrapper />
      <DetailsContainer
        isFlatSpotpicker={showFlatSpotpicker}
        bookingAppRedesign={bookingAppRedesign}
        isDaySelected={isDaySelected}
      >
        <Details flow={isTablet && !bookingAppRedesign ? 'row' : 'column'} style={bookingAppRedesign ? { color: 'inherit' } : undefined}>
          <PackageSession currentSession={currentSession} numSessions={numSessions} />
          <Duration value={eventType.duration} options={durationOptions} onChange={selectDuration} />
          <EventTypeLocation eventType={eventType} />
          {!hasMeetingPackage && (<Price source={eventType} />)}
        </Details>
      </DetailsContainer>
      {showRedesignRescheduleSummary ? (
        <SidebarRescheduleSummaryWrap
          data-component="sidebar-reschedule-summary"
          isDaySelected={isDaySelected}
        >
          <EnterDetailsRescheduleSummary
            variant="sidebar"
            formerTimeLabel={rescheduleFormerTimeLabel}
            newTimeLabel={i18nHook.t('booking.templates.new_time')}
            formerLineText={rescheduleFormerLine}
            newLineText=""
            showNewTime={false}
          />
        </SidebarRescheduleSummaryWrap>
      ) : (
        <ReschedulingDetails />
      )}
      {bookingAppRedesign ? (
        <ExpandableEventTypeDescription
          isFlatSpotpicker={showFlatSpotpicker}
          isDaySelected={isDaySelected}
        >
          {eventType.description}
        </ExpandableEventTypeDescription>
      ) : (
        <EventTypeDescription isFlatSpotpicker={showFlatSpotpicker}>
          {eventType.description}
        </EventTypeDescription>
      )}
      {showFlatSpotpicker && isTabletDown && !bookingAppRedesign && (<FooterLinks />)}
    </SidePanel>
  )
}

export default Sidebar
